<template>
    <div
        ref="googleMapsStage"
        class="h-76 w-full"
        :style="{ width, height }"
    >
        <!-- Google maps instance will be injected here -->
        <!--
                Do not condition the instantiation (v-if) of this tag or
                google maps will fail if the node is not here
            -->
    </div>
</template>

<script lang="ts" setup>
import { notifyError } from '@/bugsnag';
import { useGoogleMaps } from '@/services/google-maps.service';
import type { CSSProperties } from 'vue';
import { onUnmounted, ref, watchEffect } from 'vue';

let googleMapsInstance: google.maps.Map | undefined = undefined;
const googleMapsService = useGoogleMaps();
const googleMapsStage = ref<HTMLDivElement>();
const markerInstances = ref<google.maps.marker.AdvancedMarkerElement[]>([]);

const props = withDefaults(
    defineProps<{
        address?: string;
        mapCenter?: google.maps.LatLngLiteral;
        markerCoordinates?: google.maps.LatLngLiteral[];
        gestureHandling?: google.maps.MapOptions['gestureHandling'];
        zoomControl?: boolean;
        width?: CSSProperties['width'];
        height?: CSSProperties['height'];
    }>(),
    {
        width: '100%',
        height: '260px',
        gestureHandling: 'cooperative',
        markerCoordinates: () => [],
        zoomControl: true,
    },
);

const emit = defineEmits<{
    renderStatus: ['success' | 'fail'];
}>();

onUnmounted(() => {
    // Clean up the map instance when the component is unmounted
    if (googleMapsInstance) {
        googleMapsInstance.setMapTypeId('none');
        googleMapsInstance.setOptions({
            mapTypeId: 'none',
        });
        googleMapsInstance = undefined;
    }
    // Clean up the markers
    markerInstances.value.forEach(marker => {
        marker.remove();
    });
    markerInstances.value = [];
});

const needToGeocode = ref<boolean>(!!(props.address && !props.mapCenter));
const latLngCenter = ref<google.maps.LatLngLiteral | undefined>(props.mapCenter);
const latLngMarkers = ref<google.maps.LatLngLiteral[]>(props.markerCoordinates);

const DEFAULT_MAP_ZOOM = 15;

function createMapInstance() {
    if (googleMapsService.libraryLoad === 'ready' && latLngCenter.value && googleMapsStage.value) {
        googleMapsInstance = googleMapsService.createMap(googleMapsStage.value, {
            center: latLngCenter.value,
            zoom: DEFAULT_MAP_ZOOM,
            mapId: import.meta.env.VITE_GOOGLE_MAPS_STYLE_ID,
            zoomControl: props.zoomControl,
            disableDefaultUI: true,
            gestureHandling: props.gestureHandling,
        });
        return;
    }
    console.error('Map instance could not be created');
    emit('renderStatus', 'fail');
}

function refreshMap() {
    const mapCenter = latLngCenter.value;
    const markerCoordinates = latLngMarkers.value;
    createMapInstance();

    if (!googleMapsInstance) {
        notifyError('attempted to refresh map without a valid instance', 'GoogleMap:refreshMap');
        emit('renderStatus', 'fail');
        return;
    }
    if (mapCenter) {
        googleMapsInstance.setCenter(mapCenter);
        googleMapsInstance.setZoom(DEFAULT_MAP_ZOOM);
    }

    markerInstances.value.forEach(marker => {
        marker.remove();
    });

    markerInstances.value =
        (markerCoordinates
            ?.map(position => {
                const marker = googleMapsService.attachMarker({
                    position,
                    map: googleMapsInstance,
                });
                return marker;
            })
            ?.filter(Boolean) as google.maps.marker.AdvancedMarkerElement[]) || [];
    emit('renderStatus', 'success');
}

watchEffect(() => {
    const libraryReady = googleMapsService.libraryLoad === 'ready';

    if (!libraryReady) {
        return;
    }

    if (!needToGeocode.value && googleMapsStage.value && latLngCenter.value && latLngMarkers.value) {
        refreshMap();
    }
    if (needToGeocode.value && props.address) {
        googleMapsService
            .getLatLngFromAddress(props.address)
            .then(latLng => {
                if (latLng) {
                    needToGeocode.value = false;
                    latLngCenter.value = latLng;
                    latLngMarkers.value = [latLng];
                    refreshMap();
                }
            })
            .catch(error => {
                notifyError(error, 'GoogleMap:getLatLngFromAddress', { address: props.address });
                emit('renderStatus', 'fail');
            });
    }
});
</script>
